import { KeyAndScale } from '@arpeggi/shared'
import { destroy, Instance, types } from 'mobx-state-tree'
import { IArpMedia } from '../../../../features/arp-registry/ArpMedia'
import ArpRegistry from '../../../../features/arp-registry/ArpRegistry'
import { ForDirectExtend } from '../../../../features/mobx'
import { cloneAudioBuffer } from '../../../audio/engine/EngineSampleUtils'
import { ADSRStore } from './ADSRStore'

/**
 * Stores information about the sample and any properties that affect the sample when rendered with the Audio Engine.
 */
const _SampleWithEffectsStore = types
  .model({
    /** This is the index of the sample within ARP. */
    sampleIndex: -1,
    /** What type of media the underlying sample represents */
    mediaType: types.optional(types.enumeration(['sample', 'stem', 'song']), 'sample'),
    adsr: types.maybeNull(ADSRStore),
    autoRelease: false,
    /** As a percent (0 - 1) where in the sample to begin playback
     * Must be less than self.end always */
    start: 0,
    /** As a percent (0 - 1) where in the sample to begin playback
     * Must be greater than self.start always */
    end: 1,
    /** the factor the sample is stretch by */
    warp: 1,
    /** the number of semitones to transpose by (can be negative or positive) */
    transpose: 0,
    /** whether the sample should play back reversed */
    reverse: false,
    gain: 0.5,
    /**
     * The key of the sample (type is KeyAndScale.Key). This needs to be a variable, and not a view
     * because it is used in the offline renderer, which only gets access to a snapshot of the store.
     */
    key: 0
  })
  .views((self) => ({
    async getArpMediaAsync(): Promise<IArpMedia> {
      return await ArpRegistry.getByIdAsync(self.sampleIndex)
    },
    getArpMedia(): IArpMedia {
      return ArpRegistry.retrieveFromCache(self.sampleIndex)
    }
  }))
  .views((self) => ({
    /**
     * Gets the array buffer for the SampleWithEffectsStore, reversing if self.reverse === true
     */
    async getSampleBuffer(): Promise<AudioBuffer> {
      const arpMedia = await this.getArpMediaAsync()
      const audioBuffer = await arpMedia.fetchAudioBuffer()
      if (!self.reverse) return audioBuffer
      const reverseBuffer = cloneAudioBuffer(audioBuffer)
      let channel = 0
      while (channel < reverseBuffer.numberOfChannels) {
        Array.prototype.reverse.call(reverseBuffer.getChannelData(channel))
        channel++
      }
      return reverseBuffer
    }
  }))
  .views((self) => ({
    /**
     * Convenience function for easily getting the SampleArpMedia associated with this sample.
     * Note: This only works if this sample has been added to a song!
     */
    getMetadata(): IArpMedia {
      // Intentionally using "this" instead of "self" here.
      // See https://mobx-state-tree.js.org/tips/typescript
      return this.getArpMedia(self.sampleIndex)
    },
    /** Returns a double of the number of seconds the sample is */
    async getFullSampleDurationSeconds(): Promise<number> {
      const sampleBuffer = await self.getSampleBuffer()
      return sampleBuffer.duration
    },
    /** Returns a double of the number of seconds this object is
     *  NOTE: this does take into account self.start and self.end
     */
    async getDurationInSeconds(): Promise<number> {
      const bufferDuration = await this.getSampleDurationInSeconds()
      return (self.end - self.start) * bufferDuration
    }
  }))
  .actions((self) => {
    /**
     *  updates the key. This needs to be in its own actions section,
     *  so the below updateSample method can use it.
     *
     * This should not be called from outside this class.
     */
    function setKeyFromSample(sample: IArpMedia) {
      self.key = KeyAndScale.from(sample.metadata.key).key
    }
    return { setKeyFromSample }
  })
  .actions((self) => {
    /** Clones all values from the given ISampleWithEffectsStore and assigns them to this.  */
    function cloneFrom(original: ISampleWithEffectsStore) {
      updateSample(original.sampleIndex)

      self.start = original.start
      self.end = original.end
      self.autoRelease = original.autoRelease

      if (original.adsr) {
        self.adsr = ADSRStore.create({
          attack: original.adsr.attack,
          decay: original.adsr.decay,
          sustain: original.adsr.sustain,
          release: original.adsr.release
        })
      }
    }

    /** updates the sample index and asynchronously updaets the key. */
    function updateSample(sampleIndex: number) {
      self.sampleIndex = sampleIndex
      self.getArpMediaAsync().then(self.setKeyFromSample)
    }
    /** updates the sample mediaType */
    function updateMediaType(mediaType: 'sample' | 'stem' | 'song') {
      self.mediaType = mediaType
    }
    /** This has not been fully supported in the app yet. Make sure it works properly before use. */
    function updateAutoRelease(autoRelease: boolean) {
      self.autoRelease = autoRelease
    }
    /** This has not been fully supported in the app yet. Make sure it works properly before use. */
    function toggleEnvelope() {
      if (self.adsr) destroy(self.adsr)
      else {
        self.adsr = ADSRStore.create()
      }
    }
    /** This has not been fully supported in the app yet. Make sure it works properly before use. */
    function updateSampleStart(startTime: number) {
      if (startTime >= self.end) return
      self.start = startTime
    }
    /** This has not been fully supported in the app yet. Make sure it works properly before use. */
    function updateSampleEnd(endTime: number) {
      if (self.start >= endTime) return
      self.end = endTime
    }
    /** if within a clip, use the clip's updateTranspose method */
    function updateTranspose(transpose: number) {
      self.transpose = transpose
    }
    /** if within a clip, use the clip's updateGain method */
    function updateGain(gain: number) {
      self.gain = gain
    }
    /** if within a clip, use the clip's updateWarp method */
    function updateWarp(warp: number) {
      self.warp = warp
    }
    /** if within a clip, use the clip's updateReverse method */
    function updateReverse(shouldReverse: boolean) {
      self.reverse = shouldReverse
    }

    return {
      cloneFrom,
      updateSample,
      updateMediaType,
      updateAutoRelease,
      updateSampleEnd,
      updateSampleStart,
      toggleEnvelope,
      updateTranspose,
      updateGain,
      updateWarp,
      updateReverse
    }
  })

interface SampleWithEffectsStoreType extends ForDirectExtend<typeof _SampleWithEffectsStore> {}
export const SampleWithEffectsStore: SampleWithEffectsStoreType = _SampleWithEffectsStore

export interface ISampleWithEffectsStore
  extends ForDirectExtend<Instance<typeof SampleWithEffectsStore>> {}
